import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Search, Filter } from 'lucide-react';
import { API, photoUrl } from '../lib/api';

const VehiclesList = () => {
  const [vehicles, setVehicles] = useState([]);
  const [societes, setSocietes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [search, setSearch] = useState('');
  const [statutFilter, setStatutFilter] = useState('');
  const [societeFilter, setSocieteFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    fetchVehicles();
  }, [statutFilter, societeFilter, typeFilter, search]);

  const fetchData = async () => {
    try {
      const societesRes = await axios.get(`${API}/societes`, { timeout: 20000 });
      setSocietes(societesRes.data);
      await fetchVehicles();
    } catch (error) {
      console.error('Error fetching data:', error);
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  };

  const fetchVehicles = async () => {
    setLoadError(false);
    try {
      const params = new URLSearchParams();
      if (statutFilter) {
        params.append('statut', statutFilter);
      }
      if (societeFilter) params.append('societe_id', societeFilter);
      if (typeFilter) params.append('type_vehicule', typeFilter);
      if (search) params.append('search', search);

      const res = await axios.get(`${API}/vehicles?${params.toString()}`, { timeout: 20000 });
      // Exclure les véhicules vendus par défaut (page dédiée /vehicles/sold)
      const filtered = statutFilter
        ? res.data
        : res.data.filter(v => v.statut !== 'vendu');
      setVehicles(filtered);
    } catch (error) {
      console.error('Error fetching vehicles:', error);
      setLoadError(true);
    }
  };

  if (loading) {
    return <div className="text-center py-12">Chargement...</div>;
  }

  const voitures = vehicles.filter(v => v.type_vehicule === 'voiture' || !v.type_vehicule);
  const utilitaires = vehicles.filter(v => v.type_vehicule === 'utilitaire');

  const renderVehicleCard = (vehicle) => (
    <Link
      key={vehicle.id}
      to={`/vehicles/${vehicle.id}`}
      data-testid={`vehicle-card-${vehicle.immatriculation}`}
      className="vehicle-card overflow-hidden"
    >
      <div className="h-48 bg-slate-200 overflow-hidden">
        {vehicle.photos && vehicle.photos[0] ? (
          <img
            src={photoUrl(vehicle.photos[0])}
            alt={`${vehicle.marque} ${vehicle.modele}`}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-slate-400">
            <span>Pas de photo</span>
          </div>
        )}
      </div>
      
      <div className="p-5">
        <div className="flex justify-between items-start mb-3">
          <div>
            <h3 className="font-semibold text-lg text-slate-900">{vehicle.marque} {vehicle.modele}</h3>
            <p className="text-sm text-slate-600 font-mono">{vehicle.immatriculation}</p>
            {vehicle.type_vehicule === 'utilitaire' && (
              <span className="inline-block mt-1 px-2 py-0.5 text-xs bg-blue-100 text-blue-800 rounded">
                Utilitaire
              </span>
            )}
          </div>
          <span className={`status-badge status-${vehicle.statut}`}>
            {vehicle.statut === 'en_stock' ? 'Stock' : vehicle.statut === 'vendu' ? 'Vendu' : 'Réservé'}
          </span>
        </div>
        
        <div className="space-y-1 text-sm">
          <div className="flex justify-between">
            <span className="text-slate-600">Année:</span>
            <span className="font-mono text-slate-900">{vehicle.annee}</span>
          </div>
          {vehicle.prix_vente && (
            <>
              <div className="flex justify-between">
                <span className="text-slate-600">Prix vente TTC:</span>
                <span className="font-mono text-green-600 font-semibold">{vehicle.prix_vente.toLocaleString('fr-FR')} €</span>
              </div>
              {vehicle.tva_applicable && (
                <div className="flex justify-between">
                  <span className="text-slate-500 text-xs">dont HT :</span>
                  <span className="font-mono text-slate-500 text-xs">{Math.round(vehicle.prix_vente / 1.20).toLocaleString('fr-FR')} €</span>
                </div>
              )}
            </>
          )}
          <div className="flex justify-between">
            <span className="text-slate-600">Société:</span>
            <span className="text-slate-900 text-xs">{vehicle.societe_nom}</span>
          </div>
        </div>
      </div>
    </Link>
  );

  return (
    <div data-testid="vehicles-list-page">
      {loadError && (
        <div className="mb-6 bg-amber-50 border border-amber-200 rounded-md p-4 flex items-center justify-between" data-testid="vehicles-error-banner">
          <div>
            <p className="text-amber-900 font-semibold">Impossible de charger la liste</p>
            <p className="text-amber-700 text-sm">Vérifiez votre connexion ou réessayez.</p>
          </div>
          <button
            onClick={() => { setLoading(true); fetchData(); }}
            data-testid="vehicles-retry-btn"
            className="px-4 py-2 bg-amber-600 text-white rounded-md hover:bg-amber-700 transition-colors text-sm font-semibold"
          >
            Réessayer
          </button>
        </div>
      )}
      <div className="flex justify-between items-center mb-8">
        <div>
          <h2 className="text-3xl font-bold tracking-tight text-slate-900" style={{fontFamily: 'Manrope'}}>Véhicules</h2>
          {!loadError && (
            <p className="text-slate-600 mt-2">
              {vehicles.length} véhicule(s) en stock — <Link to="/vehicles/sold" className="text-slate-900 underline hover:text-red-600 transition-colors">voir les véhicules vendus</Link>
            </p>
          )}
        </div>
        <Link
          to="/vehicles/new"
          data-testid="add-vehicle-btn"
          className="px-6 py-3 bg-slate-900 text-white rounded-md hover:bg-slate-800 font-medium transition-colors shadow-sm"
        >
          + Ajouter un véhicule
        </Link>
      </div>

      <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-6 mb-6">
        <div className="flex flex-wrap gap-4">
          <div className="flex-1 min-w-[250px]">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={18} />
              <input
                type="text"
                data-testid="search-input"
                placeholder="Rechercher par immatriculation, marque, modèle..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-slate-900"
              />
            </div>
          </div>
          
          <div className="flex gap-4">
            <select
              data-testid="type-filter"
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              className="px-4 py-2 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-slate-900"
            >
              <option value="">Tous les types</option>
              <option value="voiture">Voitures</option>
              <option value="utilitaire">Utilitaires</option>
            </select>

            <select
              data-testid="status-filter"
              value={statutFilter}
              onChange={(e) => setStatutFilter(e.target.value)}
              className="px-4 py-2 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-slate-900"
            >
              <option value="">En stock + Réservés</option>
              <option value="en_stock">En stock</option>
              <option value="reserve">Réservé</option>
            </select>

            <select
              data-testid="societe-filter"
              value={societeFilter}
              onChange={(e) => setSocieteFilter(e.target.value)}
              className="px-4 py-2 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-slate-900"
            >
              <option value="">Toutes les sociétés</option>
              {societes.map(societe => (
                <option key={societe.id} value={societe.id}>{societe.nom}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {vehicles.length === 0 ? (
        <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-12 text-center">
          <p className="text-slate-500 mb-4">Aucun véhicule trouvé</p>
          <Link to="/vehicles/new" data-testid="add-vehicle-empty-btn" className="inline-block px-6 py-3 bg-slate-900 text-white rounded-md hover:bg-slate-800 transition-colors">
            Ajouter un véhicule
          </Link>
        </div>
      ) : (
        <div className="space-y-8">
          {/* Section Voitures */}
          {voitures.length > 0 && (
            <div>
              <div className="flex items-center gap-3 mb-4">
                <div className="flex items-center gap-2">
                  <div className="w-10 h-10 bg-slate-900 rounded-lg flex items-center justify-center">
                    <span className="text-white text-xl">🚗</span>
                  </div>
                  <h3 className="text-2xl font-bold text-slate-900" style={{fontFamily: 'Manrope'}}>
                    Voitures
                  </h3>
                </div>
                <span className="px-3 py-1 bg-slate-100 text-slate-700 rounded-full text-sm font-semibold">
                  {voitures.length}
                </span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {voitures.map(vehicle => renderVehicleCard(vehicle))}
              </div>
            </div>
          )}

          {/* Section Utilitaires */}
          {utilitaires.length > 0 && (
            <div>
              <div className="flex items-center gap-3 mb-4">
                <div className="flex items-center gap-2">
                  <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
                    <span className="text-white text-xl">🚛</span>
                  </div>
                  <h3 className="text-2xl font-bold text-slate-900" style={{fontFamily: 'Manrope'}}>
                    Utilitaires
                  </h3>
                </div>
                <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-semibold">
                  {utilitaires.length}
                </span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {utilitaires.map(vehicle => renderVehicleCard(vehicle))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default VehiclesList;
